import { useMemo, useState } from "react";
import { LinearProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DayMeter from "./DayMeter";
import WebTour from "./WebTour";
import { progressSx } from "../actionStyles";
import { buildMeterSteps, muzakereDayProgress } from "../../../lib/meter";
import Card from "../../../components/ui/Card";
import MonthCalendar from "../../../components/ui/MonthCalendar";
import CalendarSkeleton from "../../../components/ui/CalendarSkeleton";
import MonthSummaryHeader from "./MonthSummaryHeader";
import DayEditorColumn from "./DayEditorColumn";
import DayCirclesCard from "./DayCirclesCard";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useGetMyMonthLogQuery } from "../../../api/dailyLogApi";
import { useGetMyHatimInstancesQuery } from "../../../api/hatimApi";
import MyGroupsCard from "./MyGroupsCard";
import { daysInMonth, localDateString, mondayIndex, monthKey, startOfMonth } from "../../../lib/dateUtils";
import { buildMonthDays, hatimDateSet, monthSummary } from "../../../lib/calendarDays";
import { kazaMarkers, muzakereMarkers } from "../../../lib/dayMarkers";

function Legend({ t }) {
  const items = [
    { className: "bg-terakkiPrimaryDark", label: t("calendar_legend_perfect") },
    { className: "bg-terakkiBrandCloud border border-terakkiBrandMist", label: t("calendar_legend_partial") },
    { className: "bg-terakkiSurfaceAlt border border-dashed border-terakkiBorder", label: t("calendar_legend_missed") },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-terakkiNeutral">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded ${item.className}`} aria-hidden />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export default function MonthCalendarCard() {
  const { t } = useScriptMode();
  const today = useMemo(() => new Date(), []);
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => localDateString(new Date()));

  const mk = monthKey(monthDate);
  const { data, isLoading, isFetching, refetch } = useGetMyMonthLogQuery({ monthKey: mk });
  const dailyLogs = useMemo(() => data?.data?.dailyLogs || [], [data]);

  // Takvimdeki hatim rozetleri için ayın hatimleri (sağdaki kutuyla aynı sorgu,
  // RTK Query önbelleği sayesinde tek istek).
  const { data: hatimData } = useGetMyHatimInstancesQuery({ monthKey: mk });
  const hatimDates = useMemo(
    () => hatimDateSet(hatimData?.data?.instances || []),
    [hatimData]
  );

  // Takvimde müzakere günleri: katılınan dolu, planlı içi boş simge.
  const muzakereMarks = useMemo(() => muzakereMarkers(data?.data), [data]);
  // Kaza kılınan günler: kaza dairesindeki hilal simgesiyle.
  const kazaMarks = useMemo(() => kazaMarkers(data?.data), [data]);

  const summary = useMemo(
    () => monthSummary(buildMonthDays({ monthDate, dailyLogs, today, selectedDate, hatimDates }).days),
    [monthDate, dailyLogs, today, selectedDate, hatimDates]
  );

  const selectedEntry = dailyLogs.find((entry) => entry.date === selectedDate) || null;
  const mode = useTheme().palette.mode;

  // Günün metresi: hedefler ve hatim kutuları anlık ilerlemelerini bildirir,
  // müzakere ay kaydından (planlı günler ve katılım etkinlikleri).
  const [goalsProgress, setGoalsProgress] = useState(null);
  const [hatimProgress, setHatimProgress] = useState(null);
  const meterSteps = useMemo(
    () =>
      buildMeterSteps({
        goals: goalsProgress,
        hatim: hatimProgress,
        muzakere: muzakereDayProgress(data?.data, selectedDate),
      }),
    [goalsProgress, hatimProgress, data, selectedDate]
  );
  const isFutureDay = selectedDate > localDateString(today);

  // isFetching, ay değişimindeki yeniden çekmeyi de kapsıyor (isLoading yalnızca ilk yükleme).
  const busy = isLoading || isFetching;
  const totalDays = daysInMonth(monthDate);
  const gridOffset = mondayIndex(startOfMonth(monthDate));
  const weeksInView = Math.ceil((gridOffset + totalDays) / 7);

  // Gelecek aylar açılmıyor: ileride veri girilemediği için gezinmeyi de kapatıyoruz.
  const canGoNext =
    monthDate.getFullYear() < today.getFullYear() ||
    (monthDate.getFullYear() === today.getFullYear() && monthDate.getMonth() < today.getMonth());

  const shiftMonth = (delta) => {
    if (delta > 0 && !canGoNext) return;
    const next = new Date(monthDate.getFullYear(), monthDate.getMonth() + delta, 1);
    setMonthDate(next);
    // Yeni aya geçince seçili gün o ayın içinde kalsın: bu ay ise bugün, değilse ilk gün.
    const isCurrentMonth = next.getFullYear() === today.getFullYear() && next.getMonth() === today.getMonth();
    setSelectedDate(localDateString(isCurrentMonth ? today : next));
  };

  return (
    <div className="flex flex-col gap-6 lg:h-full lg:min-h-0">
      <MonthSummaryHeader
        monthDate={monthDate}
        summary={summary}
        totalDays={totalDays}
        loading={busy}
        onPrev={() => shiftMonth(-1)}
        onNext={() => shiftMonth(1)}
        canGoNext={canGoNext}
      />

      {/* Başlığın altında daire şeridi: Katıl, hatimler, müzakereler, kaza */}
      <DayCirclesCard
        localDate={selectedDate}
        monthKey={mk}
        onChanged={refetch}
        onHatimProgressChange={setHatimProgress}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_680px] gap-6 items-start lg:items-stretch lg:flex-1 lg:min-h-0">
        {/* Sol sütun: takvim */}
        <div className="flex flex-col gap-6 min-w-0 lg:self-start">
        <Card data-tour="days" className="motion-safe:animate-rise-in [animation-delay:.12s]">
          {/* Ay değiştirirken: ince activity çubuğu + ızgara iskeleti.
              Yeni ayın verisi gelene kadar eski ayı göstermek yanıltıcı olurdu. */}
          <div className="relative">
            {busy && (
              <LinearProgress
                className="!absolute -top-1 left-0 right-0 !h-0.5 !rounded"
                sx={progressSx(mode)}
              />
            )}
            {busy ? (
              <CalendarSkeleton weeks={weeksInView} offset={gridOffset} />
            ) : (
              <MonthCalendar
                monthDate={monthDate}
                dailyLogs={dailyLogs}
                selectedDate={selectedDate}
                today={today}
                hatimDates={hatimDates}
                muzakereMarks={muzakereMarks}
                kazaMarks={kazaMarks}
                onDayClick={setSelectedDate}
              />
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-terakkiBorder">
            <Legend t={t} />
          </div>
        </Card>
        </div>

        {/* Takvimin sağında Hedeflerim ve gruplardaki durumum: geniş ekranda
            yan yana, aynı yükseklikte; listeleri kendi içinde kayıyor. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch lg:h-full lg:min-h-0">
          <DayEditorColumn
            localDate={selectedDate}
            entry={selectedEntry}
            loading={busy}
            onSaved={refetch}
            onProgressChange={setGoalsProgress}
          />
          <MyGroupsCard localDate={selectedDate} />
        </div>
      </div>

      {/* Günün metresi: seçili günün hedef, hatim ve müzakere ilerlemesi */}
      {!isFutureDay ? <DayMeter steps={meterSteps} loading={busy} /> : null}

      {/* İlk girişte sayfa tanıtımı (bir kez; hesap sayfasından tekrar) */}
      <WebTour ready={!busy} />
    </div>
  );
}
